import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { formatShortDateTime } from '../../shared/time';
import {
  Button,
  Callout,
  type DropFile,
  IconCheckCircle,
  PublicActions,
  PublicPage,
  PublicPrimary,
  PublicShell,
  PublicTitle,
  Row,
  Spinner,
  StepFooter,
  StepNav,
  Stepper,
  SUPPORT_EMAIL,
  Tag,
} from '../../ui/v2';
import { apiEndpoints, apiPublic } from '../../utils/apiService';
import {
  validateUploadFiles,
  validateUploadFilesBasic,
} from '../../utils/fileUploadValidation';
import { validateOnboardingRouting } from '../../utils/onboardingRoutingValidation';
import { normalizeOnboardingPayloadUrls } from '../../utils/urlInput';
import {
  type Confirmation,
  confirmationLabel,
  copiedCounts,
  draftFromView,
  type LinkView,
  markFor,
  stepsWithCopies,
} from './onboarding/link';
import {
  errorCount,
  type FieldErrors,
  firstInvalidStep,
  payloadForSubmit,
  type StepKey,
  stepsFor,
  summarize,
  validateStep,
} from './onboarding/model';
import {
  EventsStep,
  FaqsStep,
  OrdersStep,
  PeopleStep,
  ProvenanceContext,
  ReservationsStep,
  RestaurantStep,
  ReviewStep,
  ServicesStep,
} from './onboarding/steps';
import { type SaveState, useLinkDraft } from './onboarding/useLinkDraft';

/**
 * Store onboarding by link — `/store-onboarding/<token>`.
 *
 * The same eight steps as `/store-onboarding`, for a location a group adds
 * (or a restaurant the team sends a link to). Whoever holds the link can
 * fill it in, with no account. Answers save to the server as you type, so
 * the link picks up where it left off on any device. It may start from
 * another location's answers: those carry a "From Main St" tag until
 * changed. Each step is confirmed before the next; Submit sends it to the
 * Ask Arthur team, and the link turns read-only.
 */
const StoreOnboardingLinkPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  return <LinkGate key={token} token={token} />;
};

export default StoreOnboardingLinkPage;

type GateState =
  | { kind: 'checking' }
  | { kind: 'ok'; view: LinkView }
  | { kind: 'missing' }
  | { kind: 'error' };

const LinkGate: React.FC<{ token: string }> = ({ token }) => {
  const [state, setState] = useState<GateState>({ kind: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'checking' });
    (async () => {
      try {
        const res = await apiPublic.get(
          apiEndpoints.publicOnboardingLink(token),
        );
        const body = (await res.json().catch(() => null)) as LinkView | null;
        if (cancelled) return;
        if ((res.ok || res.status === 410) && body?.state) {
          setState({ kind: 'ok', view: body });
        } else {
          setState({ kind: res.status === 404 ? 'missing' : 'error' });
        }
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  if (state.kind === 'checking') {
    return (
      <Notice title="Opening your onboarding…">
        <div
          className="mt-6 flex items-center gap-3 text-v2-13 text-v2-fg-2"
          role="status"
          aria-live="polite"
        >
          <Spinner className="h-4 w-4" /> One moment
        </div>
      </Notice>
    );
  }
  if (state.kind === 'error') {
    return (
      <Notice
        title="Couldn’t open this link"
        lede="We couldn’t load your answers just now. Nothing is lost — try again in a moment."
      >
        <PublicActions className="mt-6">
          <PublicPrimary onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </PublicPrimary>
        </PublicActions>
      </Notice>
    );
  }
  if (state.kind === 'missing') {
    return (
      <Notice
        title="This link isn’t valid"
        lede="Check that the whole link was copied, or ask whoever sent it for a new one."
      >
        <AskForLink />
      </Notice>
    );
  }

  const { view } = state;
  if (view.state === 'revoked' || view.state === 'expired') {
    return (
      <Notice
        title={
          view.state === 'expired'
            ? 'This link has expired'
            : 'This link was turned off'
        }
        lede={
          view.state === 'expired'
            ? 'Links stop working three months after they were last used. Ask whoever sent it for a new one; the answers are kept.'
            : 'It was replaced or turned off by whoever sent it. Ask them for the current link.'
        }
      >
        <AskForLink />
      </Notice>
    );
  }
  if (view.state !== 'open') return <Submitted view={view} />;
  return <LinkIntake token={token} view={view} />;
};

const Notice: React.FC<{
  title: string;
  lede?: string;
  children?: React.ReactNode;
}> = ({ title, lede, children }) => (
  <PublicPage>
    <PageMeta
      title={`${title} · AskArthur`}
      description="Restaurant onboarding"
    />
    <PublicShell width="form">
      <PublicTitle lede={lede}>{title}</PublicTitle>
      {children}
    </PublicShell>
  </PublicPage>
);

const AskForLink: React.FC = () => (
  <p className="mt-6 text-v2-13 text-v2-fg-3">
    Questions?{' '}
    <a
      href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Onboarding link')}`}
      className="text-v2-fg-2 underline underline-offset-2"
    >
      Email {SUPPORT_EMAIL}
    </a>
    .
  </p>
);

// ---------------------------------------------------------------------------
// The intake
// ---------------------------------------------------------------------------

type ServerFile = DropFile & { serverId?: number };

const fileError = (file: File, message: string) =>
  message.replace(`${file.name}: `, '');

const saveNote = (state: SaveState, savedAt: number | null) => {
  if (state === 'saving') return 'Saving…';
  if (state === 'error') return 'Couldn’t save. Trying again…';
  if (savedAt)
    return `Saved ${formatShortDateTime(new Date(savedAt).toISOString())}`;
  return 'Your answers save as you go';
};

const LinkIntake: React.FC<{ token: string; view: LinkView }> = ({
  token,
  view: initialView,
}) => {
  const [view, setView] = useState(initialView);
  const [addressResolving, setAddressResolving] = useState(false);
  const { draft, update, save, saveState, savedAt } = useLinkDraft(
    token,
    () => draftFromView(initialView),
    addressResolving,
  );
  const [confirmed, setConfirmed] = useState<
    Partial<Record<StepKey, Confirmation>>
  >(initialView.confirmed_steps ?? {});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [files, setFiles] = useState<ServerFile[]>(() =>
    (initialView.files ?? []).map((f) => ({
      id: `server-${f.id}`,
      serverId: f.id,
      name: f.original_filename,
      size: f.size_bytes,
      status: 'ready',
    })),
  );
  const removed = useRef(new Set<string>());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const compactHeadingRef = useRef<HTMLDivElement>(null);

  const copied = useMemo(() => view.copied ?? {}, [view.copied]);
  const source = view.copied_from_name;
  const copiedSteps = useMemo(() => stepsWithCopies(copied), [copied]);
  const name = view.location_name || 'this location';

  const steps = useMemo(
    () =>
      stepsFor(draft.payload).map((s) => ({
        ...s,
        sub: confirmed[s.key]
          ? 'Confirmed'
          : source && copiedSteps.has(s.key)
            ? `From ${source}`
            : undefined,
      })),
    [draft.payload, confirmed, source, copiedSteps],
  );
  const idx = Math.max(
    0,
    steps.findIndex((s) => s.key === draft.step),
  );
  const step = steps[idx];
  const isReview = step.key === 'review';
  const confirmable = steps.filter((s) => s.key !== 'review');
  const confirmedKeys = confirmable
    .filter((s) => confirmed[s.key])
    .map((s) => s.key);

  const mark = useCallback(
    (key: string) => {
      const m = markFor(key, copied, draft.payload);
      if (!m) return null;
      return m.kind === 'copied' ? (
        <Tag tone="accent">From {source}</Tag>
      ) : (
        <Tag tone="warning">
          {m.was != null ? `Changed · ${source}: ${m.was}` : 'Changed'}
        </Tag>
      );
    },
    [copied, draft.payload, source],
  );

  const go = useCallback(
    (key: StepKey) => {
      if (addressResolving) return;
      setErrors({});
      setActionError(null);
      update((d) => ({
        ...d,
        step: key,
        reviewed: d.reviewed || key === 'review',
      }));
      window.scrollTo({ top: 0 });
      requestAnimationFrame(() => {
        const wide = window.matchMedia?.('(min-width: 1024px)').matches;
        (wide ? headingRef : compactHeadingRef).current?.focus();
      });
    },
    [addressResolving, update],
  );

  const showErrors = (e: FieldErrors) => {
    setErrors(e);
    const first = Object.keys(e).find((k) => e[k]);
    if (!first) return;
    requestAnimationFrame(() => {
      const slot = document.querySelector<HTMLElement>(
        `[data-field="${first}"]`,
      );
      slot?.scrollIntoView?.({ block: 'center' });
      slot
        ?.querySelector<HTMLElement>('input, select, textarea, button')
        ?.focus();
    });
  };

  /** After confirming `from`: the next step still to confirm, else review. */
  const nextAfter = (
    from: StepKey,
    done: Partial<Record<StepKey, Confirmation>>,
  ): StepKey => {
    const order = confirmable.map((s) => s.key);
    const at = order.indexOf(from);
    const rest = [...order.slice(at + 1), ...order.slice(0, at)];
    return rest.find((k) => !done[k]) ?? 'review';
  };

  const confirmStep = async () => {
    if (addressResolving) return;
    const e = validateStep(step.key, draft);
    if (errorCount(e) > 0) return showErrors(e);
    setBusy(true);
    setActionError(null);
    try {
      const res = await save({
        confirm: { step: step.key, name: draft.payload.primary_contact.name },
      });
      setConfirmed(res.confirmed_steps);
      go(nextAfter(step.key, res.confirmed_steps));
    } catch {
      setActionError(
        'We couldn’t confirm this step just now. Your answers are kept — try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const invalid = firstInvalidStep(draft);
    if (invalid) {
      go(invalid.step);
      showErrors(invalid.errors);
      return;
    }
    const open = confirmable.find((s) => !confirmed[s.key]);
    if (open) {
      go(open.key);
      setActionError('Confirm this step before you submit.');
      return;
    }
    const payload = normalizeOnboardingPayloadUrls(payloadForSubmit(draft));
    const routingError = validateOnboardingRouting(payload);
    if (routingError) {
      setActionError(routingError);
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiPublic.post(
        apiEndpoints.publicOnboardingLinkSubmit(token),
        { payload },
      );
      const body = (await res.json().catch(() => null)) as
        | (LinkView & { detail?: string })
        | null;
      if (!res.ok || !body?.state) {
        setActionError(
          body?.detail ??
            'We couldn’t submit just now. Your answers are kept — try again.',
        );
        return;
      }
      setView(body);
      window.scrollTo({ top: 0 });
    } catch {
      setActionError(
        'Can’t reach AskArthur. Your answers are saved — try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  const addFiles = (list: File[]) => {
    list.forEach(async (file) => {
      const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`;
      const basic = validateUploadFilesBasic([file]);
      setFiles((f) => [
        ...f,
        {
          id,
          name: file.name,
          size: file.size,
          status: basic ? 'failed' : 'checking',
          error: basic ? fileError(file, basic) : undefined,
        },
      ]);
      if (basic) return;
      const settle = (patch: Partial<ServerFile>) =>
        setFiles((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));
      const err = await validateUploadFiles([file]);
      if (err) return settle({ status: 'failed', error: fileError(file, err) });
      const form = new FormData();
      form.append('files', file);
      try {
        const res = await apiPublic.postForm(
          apiEndpoints.publicOnboardingLinkFiles(token),
          form,
        );
        const body = (await res.json().catch(() => null)) as
          | { id: number }[]
          | Record<string, string>
          | null;
        if (!res.ok || !Array.isArray(body)) {
          const reason =
            body && !Array.isArray(body) ? Object.values(body)[0] : null;
          return settle({
            status: 'failed',
            error: reason ? String(reason) : 'Couldn’t upload. Try again.',
          });
        }
        const serverId = body[0]?.id;
        if (removed.current.has(id)) {
          if (serverId != null) deleteOnServer(serverId);
          return;
        }
        settle({ status: 'ready', serverId });
      } catch {
        settle({ status: 'failed', error: 'Couldn’t upload. Try again.' });
      }
    });
  };

  const deleteOnServer = (serverId: number) =>
    void apiPublic
      .delete(apiEndpoints.publicOnboardingLinkFile(token, serverId))
      .catch(() => null);

  const removeFile = (id: string) => {
    const file = files.find((f) => f.id === id);
    setFiles((f) => f.filter((x) => x.id !== id));
    if (file?.serverId != null) deleteOnServer(file.serverId);
    // Still uploading: delete it once the upload lands.
    else removed.current.add(id);
  };

  if (view.state !== 'open') return <Submitted view={view} />;

  const clearError = (k: string) =>
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  const stepProps = { draft, update, errors, clearError, goToStep: go };
  const counts = copiedCounts(step.key, copied, draft.payload);
  const checkingFiles = files.some((f) => f.status === 'checking');
  const badFiles = files.filter((f) => f.status === 'failed').length;
  const note =
    !isReview && counts.copied > 0
      ? `${counts.copied} ${counts.copied === 1 ? 'answer' : 'answers'} copied${
          counts.changed ? ` · ${counts.changed} changed` : ''
        }`
      : undefined;

  return (
    <PublicPage>
      <PageMeta
        title={`${step.label} · ${name} onboarding · AskArthur`}
        description="Restaurant onboarding"
      />
      <PublicShell
        width="wide"
        flush
        aside={
          <span role="status" aria-live="polite">
            {saveNote(saveState, savedAt)}
          </span>
        }
      >
        <div className="grid lg:grid-cols-[224px_minmax(0,1fr)]">
          <aside className="hidden border-r border-v2-line px-4 py-6 lg:block">
            <div className="sticky top-6">
              <p className="px-2 text-v2-11 font-medium text-v2-fg-4">
                {view.group_name || 'Restaurant onboarding'}
              </p>
              <p className="mt-0.5 truncate px-2 text-v2-13 font-semibold text-v2-fg">
                {draft.payload.business.restaurant_name || name}
              </p>
              <StepNav
                steps={steps}
                current={step.key}
                completed={confirmedKeys}
                onSelect={(k) => go(k)}
                selectDisabledReason={
                  addressResolving ? 'Still checking the address' : undefined
                }
                className="mt-4"
              />
            </div>
          </aside>
          <div className="min-w-0 px-5 py-6 sm:px-7 sm:py-7">
            {source && step.key === 'restaurant' && (
              <Callout
                tone="accent"
                className="mb-6"
                title={`${name} starts from ${source}’s answers`}
              >
                Check each step and confirm it. Anything specific to {name}{' '}
                starts blank
                {view.same_legal_entity
                  ? `, except the legal details: it’s the same business as ${source}.`
                  : '.'}
              </Callout>
            )}
            <div
              ref={compactHeadingRef}
              tabIndex={-1}
              className="focus:outline-none lg:hidden"
            >
              <Stepper steps={steps} current={step.key} />
            </div>
            <div className="hidden lg:block">
              <p className="v2-num text-v2-12 text-v2-fg-3">
                Step {idx + 1} of {steps.length}
              </p>
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="mt-0.5 text-v2-20 font-semibold tracking-[-0.01em] text-v2-fg focus:outline-none"
              >
                {step.label}
              </h2>
              {step.description && (
                <p className="mt-1 text-v2-13 text-v2-fg-3">
                  {step.description}
                </p>
              )}
            </div>

            <ProvenanceContext.Provider value={mark}>
              <div className="mt-7">
                {step.key === 'restaurant' && (
                  <RestaurantStep
                    {...stepProps}
                    group={view.group_name || undefined}
                    onAddressResolving={setAddressResolving}
                  />
                )}
                {step.key === 'people' && (
                  <PeopleStep
                    {...stepProps}
                    suggestions={view.suggested_contacts}
                  />
                )}
                {step.key === 'services' && <ServicesStep {...stepProps} />}
                {step.key === 'orders' && <OrdersStep {...stepProps} />}
                {step.key === 'reservations' && (
                  <ReservationsStep {...stepProps} />
                )}
                {step.key === 'events' && <EventsStep {...stepProps} />}
                {step.key === 'faqs' && (
                  <FaqsStep
                    {...stepProps}
                    files={files}
                    onAddFiles={addFiles}
                    onRemoveFile={removeFile}
                    disabled={busy}
                  />
                )}
                {step.key === 'review' && (
                  <ReviewStep
                    {...stepProps}
                    files={files}
                    onEdit={(k) => go(k)}
                    confirmations={Object.fromEntries(
                      Object.entries(confirmed).map(([k, c]) => [
                        k,
                        confirmationLabel(c as Confirmation),
                      ]),
                    )}
                  />
                )}
              </div>
            </ProvenanceContext.Provider>

            {errorCount(errors) > 1 && (
              <Callout tone="danger" className="mt-6">
                {errorCount(errors)} things need a look above before you
                confirm.
              </Callout>
            )}
            {actionError && (
              <Callout tone="danger" className="mt-6">
                {actionError}
              </Callout>
            )}
            <StepFooter
              className="mt-8"
              onBack={idx > 0 ? () => go(steps[idx - 1].key) : undefined}
              onNext={isReview ? submit : confirmStep}
              nextLabel={
                isReview ? 'Submit for review' : 'Confirm and continue'
              }
              busy={busy}
              nextDisabledReason={
                addressResolving
                  ? 'Still checking the address'
                  : (isReview || step.key === 'faqs') && badFiles > 0
                    ? `Remove the ${badFiles === 1 ? 'file that failed' : `${badFiles} files that failed`} first`
                    : (isReview || step.key === 'faqs') && checkingFiles
                      ? 'Still uploading a file'
                      : undefined
              }
              note={
                note ??
                (confirmed[step.key]
                  ? confirmationLabel(confirmed[step.key] as Confirmation)
                  : undefined)
              }
            />
          </div>
        </div>
      </PublicShell>
    </PublicPage>
  );
};

// ---------------------------------------------------------------------------
// Submitted: read-only
// ---------------------------------------------------------------------------

const Submitted: React.FC<{ view: LinkView }> = ({ view }) => {
  const navigate = useNavigate();
  const [showAnswers, setShowAnswers] = useState(false);
  const draft = useMemo(() => draftFromView(view), [view]);
  const name =
    draft.payload.business.restaurant_name ||
    view.location_name ||
    'This location';
  const confirmed = view.confirmed_steps ?? {};
  return (
    <PublicPage>
      <PageMeta
        title={`${name}’s intake is in · AskArthur`}
        description="Onboarding submitted"
      />
      <PublicShell width="form">
        <div className="flex items-start gap-3">
          <span className="mt-1 text-v2-success" aria-hidden>
            <IconCheckCircle size={22} />
          </span>
          <PublicTitle
            lede={
              view.state === 'approved'
                ? `${name} is set up. The manager has an email with the next steps.`
                : `Ask Arthur will confirm pricing${view.group_name ? ` with ${view.group_name}` : ''} and set up ${name}. You’ll get an email with the next steps.`
            }
          >
            {`${name}’s intake is in`}
          </PublicTitle>
        </div>
        <p className="mt-4 text-v2-13 text-v2-fg-3">
          This link is now read-only. To change an answer, email{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`${name} onboarding`)}`}
            className="text-v2-fg-2 underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
        <PublicActions className="mt-6">
          <Button
            variant="secondary"
            aria-expanded={showAnswers}
            onClick={() => setShowAnswers((s) => !s)}
          >
            {showAnswers ? 'Hide your answers' : 'View your answers'}
          </Button>
          <PublicPrimary onClick={() => navigate('/onboarding')}>
            Back to Onboarding
          </PublicPrimary>
        </PublicActions>
        {showAnswers && (
          <div className="mt-6 flex flex-col gap-5">
            {summarize(draft).map((s) => (
              <div key={s.step}>
                <h2 className="flex items-center gap-2 text-v2-12 font-semibold text-v2-fg-3">
                  {s.title}
                  {confirmed[s.step] && (
                    <Tag tone="success">
                      {confirmationLabel(confirmed[s.step] as Confirmation)}
                    </Tag>
                  )}
                </h2>
                <dl className="mt-1">
                  {s.rows.map((r) => (
                    <Row key={r.label} label={r.label} empty={r.value == null}>
                      {r.value ?? 'Not given'}
                    </Row>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </PublicShell>
    </PublicPage>
  );
};
